/**
 * Parser dla URL slug w formacie ILINK (backend Imperial) -> struktura React-CMS.
 *
 * **NOWY STANDARD (trailing slash discriminator):**
 *
 * Katalog jest traktowany jako katalog WTEDY i tylko wtedy, gdy URL kończy się
 * `/`. Wszystko po OSTATNIM `/` (gdy slasha-na-końcu brak) to elementy katalogu
 * (extras: actions / pages / articles).
 *
 * ```
 * {origin}/{lang}/{cat1}/{cat2}/.../{catalog}/            -> czysty katalog (TRAILING SLASH)
 * {origin}/{lang}/{cat1}/.../{catalog}/{extras}[?vars]#a  -> katalog + extras (BEZ trailing slash)
 * ```
 *
 * Gdzie `{extras}` = comma-separated lista (kolejność wg ILINK: actions ->
 * pages -> articles):
 * ```
 * ,action1,action2,pageN,pageM,art-symbol-id1,inny-symbol-id2
 * ```
 *
 * **Dlaczego trailing slash:** symbole katalogów mogą kończyć się na `-{cyfry}`
 * (np. `kategoria-bloga-2`) co kolidowało ze starym heurystycznym wykrywaniem
 * artykułów (`symbol-id`). Slash-na-końcu jest jednoznacznym sygnałem "to jest
 * katalog, nie element" - tak jak `dir/` vs `dir/plik` w systemie plików.
 *
 * **Reguła (jedyny dyskryminator path vs extras):**
 *   - `hasTrailingSlash === true`  -> WSZYSTKIE segmenty to `pathSegments`, brak extras.
 *   - `hasTrailingSlash === false` -> OSTATNI segment to extras (split po `,`),
 *     poprzednie segmenty to `pathSegments`.
 *
 * **Hard cutover (decyzja 2026-06-17):** URL katalogu BEZ trailing slash nie
 * jest już ważnym URL-em katalogu. Caller (route handler) zwraca 404 dla
 * pojedynczego segmentu bez slasha (np. `/pl/o-nas`) - poprawny URL to
 * `/pl/o-nas/`. Brak redirectów z legacy (system jeszcze nieprodukcyjny).
 *
 * **Klasyfikacja w segmencie extras** (gdy `hasTrailingSlash === false`):
 *   - `page\d+`   -> page (np. `page2`)
 *   - `.+-\d+`    -> article `{symbol, id}` (np. `nowa-funkcja-42`)
 *   - sam `\d+`   -> article `{symbol: null, id}` (np. `42`)
 *   - inaczej     -> action (np. `search`, `my-custom-search`)
 *   Niejednoznaczność: action o nazwie `my-action-3` (kreska + cyfra na końcu)
 *   zostanie sklasyfikowana jako article `{symbol: "my-action", id: 3}`.
 *   Konwencja: nazwy akcji bez numerycznego suffixu.
 *
 * **Symetria z ILINK::get() (backend PHP):** [IMPERIAL/ILINK.php::get()](../../../web/IMPERIAL/ILINK.php).
 * Backend dokleja `/` po katalogu gdy brak extras (czysty katalog), a extras
 * (gdy są) idą po `/` BEZ trailing slasha.
 */

export type ParsedArticle = {
  /** Symbol artykułu (z `prepareLink(name)` na backend) — `null` gdy URL ma sam ID. */
  symbol: string | null;
  /** Numeric ID z `imp_cms_catalog.id` lub innej tabeli per typ komponentu. */
  id: number;
};

export type ParsedSlug = {
  /** Locale z `[locale]` route param (przekazane bezpośrednio). */
  locale: string;
  /**
   * Path segmenty (catalog tree) — pełna lista włącznie z ostatnim catalog'iem.
   * Pusty array = homepage (`/pl` URL z slug=[]).
   *
   * **Przykład** `/pl/publikacje/blog/page2` -> `['publikacje', 'blog']`
   * **Przykład** `/pl/publikacje/blog/` (slash) -> `['publikacje', 'blog']`
   *
   * Caller używa: `fetchCatalogByPath(pathSegments, {lang: locale})`.
   */
  pathSegments: string[];
  /** Akcje na katalogu (np. `search`, `subscribe`). Pusty array gdy brak. */
  actions: string[];
  /** Numery stron pagera (np. `[2, 10]` z `page2,page10`). Pusty gdy brak. */
  pages: number[];
  /** Lista artykułów z `symbol-id`. Pusty gdy brak. */
  articles: ParsedArticle[];
};

/* Regex'y do KLASYFIKACJI pieces w segmencie extras (NIE do decyzji path-vs-extras
   - tę robi wyłącznie trailing slash). */
const RE_PAGE = /^page(\d+)$/;
const RE_ARTICLE = /^(.+)-(\d+)$/; //symbol-id (symbol może zawierać kreski)
const RE_ARTICLE_ID_ONLY = /^(\d+)$/; //sam ID (ILINK obsługuje, rzadkie)

/**
 * Parsuje slug array z Next.js `[...slug]` route (po `[locale]`) i wyciąga
 * actions / pages / articles z ostatniego segmentu w formacie ILINK.
 *
 * **Trailing slash:** Next.js gubi trailing slash w `[...slug]` params, więc
 * sygnał `hasTrailingSlash` MUSI być dostarczony osobno:
 *   - server (page.tsx): z nagłówka `x-imp-trailing-slash` ustawianego przez
 *     `middleware.ts` (Next config `skipTrailingSlashRedirect: true`),
 *   - client (useCatalogLocation): z `usePathname()` (browser zachowuje slash).
 *
 * @param locale Locale z `[locale]` route param (przekazany 1:1 do output)
 * @param slug Array segmentów z `[...slug]` (np. `['publikacje', 'blog', 'page2']`)
 * @param hasTrailingSlash Czy oryginalny URL kończył się `/` (katalog) - jedyny
 *                         dyskryminator path-vs-extras.
 * @returns Rozdzielona struktura — path segmenty (czyste, bez extras) + extras parsed
 */
export function parseSlug(
  locale: string,
  slug: string[],
  hasTrailingSlash: boolean,
): ParsedSlug {
  if (slug.length === 0) {
    /* Homepage — `/pl` / `/pl/` URL, brak path/extras. */
    return {
      locale,
      pathSegments: [],
      actions: [],
      pages: [],
      articles: [],
    };
  }

  /* Decode `%2C` -> `,` (i innych encoded chars) — Next.js w Turbopack 16 może
     dostarczyć slug segments z URL encoding nie odwiniętym, więc literal comma
     ',extension-X' przychodzi jako '%2Cextension-X'. Bez decoding'u split po
     `,` (klasyfikacja extras) by failed. */
  const decodedSlug = slug.map((s) => {
    try {
      return decodeURIComponent(s);
    } catch {
      return s; //malformed encoding — pass-through
    }
  });

  /* JEDYNY dyskryminator: trailing slash. Slash-na-końcu => czysty katalog
     (wszystkie segmenty to path, brak extras). Brak slasha => ostatni segment
     to extras. */
  if (hasTrailingSlash) {
    return {
      locale,
      pathSegments: decodedSlug,
      actions: [],
      pages: [],
      articles: [],
    };
  }

  const lastSeg = decodedSlug[decodedSlug.length - 1];
  /* `pathSegments` używamy DECODED — kolejne callery (fetchCatalogByPath,
     fetchPageBootstrap) i tak robią encodeURIComponent per segment, więc
     wysyłanie decoded gwarantuje normalizację (idempotent enc(dec(x))). */
  const pathSegments = decodedSlug.slice(0, -1);
  const extras = lastSeg.split(",");

  const actions: string[] = [];
  const pages: number[] = [];
  const articles: ParsedArticle[] = [];

  for (const piece of extras) {
    if (piece === "") continue; //defensive (np. wiodąca/trailing comma)

    const pageMatch = piece.match(RE_PAGE);
    if (pageMatch) {
      pages.push(parseInt(pageMatch[1], 10));
      continue;
    }

    const articleMatch = piece.match(RE_ARTICLE);
    if (articleMatch) {
      articles.push({
        symbol: articleMatch[1],
        id: parseInt(articleMatch[2], 10),
      });
      continue;
    }

    const idOnlyMatch = piece.match(RE_ARTICLE_ID_ONLY);
    if (idOnlyMatch) {
      articles.push({
        symbol: null,
        id: parseInt(idOnlyMatch[1], 10),
      });
      continue;
    }

    /* Domyślnie: action (string symbol bez numerycznego suffixu). */
    actions.push(piece);
  }

  return {
    locale,
    pathSegments,
    actions,
    pages,
    articles,
  };
}

/**
 * Helper inverse: złóż URL slug z parsed structure. Używany rzadko (większość
 * URL'i przychodzi gotowych z backendu przez ILINK), ale przydatny do:
 *   - testów (round-trip),
 *   - client-side URL building (np. paginacja: zmień page bez full refetch).
 *
 * **Trailing slash w output:** dla CZYSTEGO katalogu (brak actions/pages/articles)
 * buildSlug dokleja PUSTY segment `""` na końcu, więc `join("/")` produkuje
 * trailing slash (`['blog', '']` -> `"blog/"`). To jest sygnał "katalog" dla
 * routingu (symetryczne z `parseSlug` + ILINK backend). Gdy są extras - segment
 * extras idzie na końcu BEZ trailing slasha.
 *
 * **Smart prefix dla extras** — wiodące `,` w segmencie extras jest WYMAGANE
 * TYLKO gdy są akcje, bo `search` (akcja) bez `,` byłaby parsowana jako kawałek
 * niejednoznaczny. Pages (`page2`) i artykuły (`symbol-42`, sam `42`) są
 * jednoznaczne BEZ `,`. Spójność z PHP backend:
 * [IMPERIAL/ILINK.php::get()](../../../web/IMPERIAL/ILINK.php).
 *
 * **Nie buduje** origin/lang prefix — to add'uje caller (np. `/${locale}/${result}`).
 * Zwraca path tylko, bez query string ani anchor.
 *
 * @returns Slug array gotowy do `join("/")` (trailing `""` dla czystego katalogu).
 */
export function buildSlug(parsed: Omit<ParsedSlug, "locale">): string[] {
  const segments: string[] = [...parsed.pathSegments];

  const extras: string[] = [];
  for (const action of parsed.actions) extras.push(action);
  for (const page of parsed.pages) extras.push(`page${page}`);
  for (const article of parsed.articles) {
    if (article.symbol) extras.push(`${article.symbol}-${article.id}`);
    else extras.push(String(article.id));
  }

  if (extras.length > 0) {
    const needsLeadingComma = parsed.actions.length > 0;
    segments.push((needsLeadingComma ? "," : "") + extras.join(","));
    return segments;
  }

  /* Czysty katalog -> trailing slash przez pusty segment końcowy. */
  segments.push("");
  return segments;
}
